import moment from "moment";
import backEndApi from "@src/api/backEndApi";

/** 서버가 200이 아닌 응답을 준 경우 던지는 에러 (message/detail 을 alertWarning 에 그대로 전달 가능) */
export class ApiError extends Error {
    constructor(result) {
        super(result?.data?.message ?? "api error");
        this.detail = typeof result?.data?.detail === "string" ? result.data.detail : "";
    }
}

function assertOk(result) {
    if (result.status != 200) throw new ApiError(result);
    return result.data;
}

export async function fetchScheduleList() {
    return assertOk(await backEndApi.equipControl.getSchdCtrlList());
}

/** 상세 조회 + 화면 표시용 포맷 변환 */
export async function fetchScheduleDetail(schdCtrlIdx) {
    const data = assertOk(await backEndApi.equipControl.getEquipCtrlSchDetail(schdCtrlIdx));
    const fmt = (v, f) => (v ? moment(v).format(f) : null);
    return {
        ...data,
        regDate: fmt(data.regDate, "YYYY-MM-DD HH:mm:ss"),
        modDate: fmt(data.modDate, "YYYY-MM-DD HH:mm:ss"),
        startDate: fmt(data.startDate, "YYYY-MM-DD"),
        endDate: fmt(data.endDate, "YYYY-MM-DD"),
        events: (data.events || []).map((item) => ({
            ...item,
            regDate: moment(item.regDate).format("YYYY-MM-DD HH:mm:ss"),
        })),
        targets: data.targets || [],
        groups: data.groups || [],
        equips: data.equips || [],
        exceptConfig: data.exceptConfig || [],
        repeatConfig: data.repeatConfig || {},
    };
}

export function newScheduleTemplate() {
    return {
        schdCtrlName: null,
        startDate: null,
        endDate: null,
        zoneIdx: null,
        description: null,
        enabled: "Y",
        targets: [],
        groups: [],
        equips: [],
        repeatConfig: {},
        exceptConfig: [],
        events: [],
    };
}

/** 올해 공휴일 → 예외일 형태 (신규 스케줄 기본값). 실패해도 빈 배열 */
export async function fetchDefaultHolidays() {
    try {
        const result = await backEndApi.calendar.getHolidayList({
            baseDate: String(new Date().getFullYear()),
            holiTypeCode: "",
        });
        if (result.status == 200 && Array.isArray(result.data)) {
            return result.data
                .map((item) => ({ exceptName: item.holiName, exceptDt: item.baseDate, enabled: "Y" }))
                .sort((a, b) => new Date(a.exceptDt) - new Date(b.exceptDt));
        }
    } catch (e) {
        console.error(e);
    }
    return [];
}

/* ------------------------------------------------------------------ */
/* 이름 조회 캐시 (설비/그룹/설비유형 메타). 페이지 진입 시 resetCaches() */
/* ------------------------------------------------------------------ */
let equipNameCache = {};
let groupNameCache = null;
let allEquipCache = null;
let equipTypeMetaCache = {};

export function resetCaches() {
    equipNameCache = {};
    groupNameCache = null;
    allEquipCache = null;
    equipTypeMetaCache = {};
}

/** 설비유형별 설비 목록 [{equipIdx, equipType, equipName}] */
export async function fetchEquipsOfType(equipType) {
    if (!equipNameCache[equipType]) {
        equipNameCache[equipType] = backEndApi.equips
            .getEquipList(equipType)
            .then((r) => (r.status == 200 ? r.data : []))
            .catch(() => []);
    }
    return equipNameCache[equipType];
}

/** 전체 그룹 목록 [{sysNodeIdx, sysNodeName, equipType}] */
export async function fetchGroups() {
    if (!groupNameCache) {
        groupNameCache = backEndApi.sysNode
            .getSysNodeList({ nodeType: "Group" })
            .then((r) => (r.status == 200 ? r.data : []))
            .catch(() => []);
    }
    return groupNameCache;
}

/** 전체 설비 목록 (이력 로그의 설비명 매핑용) */
export async function fetchAllEquips() {
    if (!allEquipCache) {
        allEquipCache = backEndApi.equipInfo
            .getEquipList()
            .then((r) => (r.status == 200 ? r.data : []))
            .catch(() => []);
    }
    return allEquipCache;
}

/** 상세 데이터의 groups/equips 에 표시용 이름(sysNodeName/equipName)을 채운다 */
export async function fillTargetNames(detail) {
    const types = [...new Set((detail.targets || []).map((t) => t.equipType))];
    const [groups, ...equipLists] = await Promise.all([fetchGroups(), ...types.map(fetchEquipsOfType)]);
    const equips = [].concat(...equipLists);
    detail.groups.forEach((g) => {
        const found = groups.find((x) => x.sysNodeIdx == g.sysNodeIdx);
        g.sysNodeName = found ? found.sysNodeName : "";
    });
    detail.equips.forEach((e) => {
        const found = equips.find((x) => x.equipIdx == e.equipIdx);
        e.equipName = found ? found.equipName : "";
    });
    return detail;
}

async function fetchEquipTypeMeta(equipType) {
    if (!equipTypeMetaCache[equipType]) {
        equipTypeMetaCache[equipType] = backEndApi.equipTypes
            .getEquipType(equipType, "Y")
            .then((r) => (r.status == 200 ? r.data : null))
            .catch(() => null);
    }
    return equipTypeMetaCache[equipType];
}

function commandValueText(prop, value) {
    if (prop && prop.encodeType === "Enum" && prop.encodeDesc) {
        try {
            const parsed = JSON.parse(prop.encodeDesc);
            const text = Array.isArray(parsed) ? parsed[Number(value)] : parsed[value];
            if (text !== undefined) return String(text);
        } catch (e) {
            // encodeDesc 가 JSON 이 아니면 원본 값을 그대로 표시
        }
    }
    return String(value);
}

/**
 * 이벤트별 "제어모드 · 설정값" 라벨 계산. 설비유형 메타 조회 실패 시 코드 원문으로 대체.
 * @returns {Promise<string[]>} events 와 같은 순서의 라벨 배열
 */
export async function resolveEventLabels(events) {
    return Promise.all(
        (events || []).map(async (event) => {
            if (!event.ctrlMode) return "-";
            const meta = event.equipType ? await fetchEquipTypeMeta(event.equipType) : null;
            const mode = meta?.ctrlModes?.find((m) => m.ctrlMode == event.ctrlMode);
            const parts = [mode?.ctrlModeName ?? event.ctrlMode];

            let command = event.ctrlCommand;
            if (typeof command === "string") {
                try {
                    command = JSON.parse(command);
                } catch (e) {
                    command = null;
                }
            }
            if (command && typeof command === "object") {
                Object.keys(command).forEach((code) => {
                    const prop = meta?.ioProps?.find((p) => p.propCode === code);
                    parts.push(commandValueText(prop, command[code]));
                });
            }
            return parts.join(" · ");
        }),
    );
}
